import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { Grid, Skeleton, Typography } from '@mui/material';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import apis from '@src/apis';
import {
  GLOBAL_PAYMENT_METHOD,
  PAYMENT_METHOD_TYPE,
} from '@src/constants/payment';
import { COLOR } from '@src/styles/color';
import {
  COUNTRY,
  PAGINATION_LIMIT,
  SMS_IDENTITY_PROVIDER,
} from '@src/constants';
import { AUTO_RENEW_STATUS } from '@src/constants/subscription';
import { getPackageDuration } from '@src/services/package';
import { DURATION } from '@src/constants/package';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { StyledPaymentMethods } from './index.style';
import PaymentMethodItem from './PaymentMethodItem';

const PaymentMethods = ({ isAutoRenew }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const dispatch = useDispatch();
  const { paymentMethod: selectedPaymentMethod, package: packageState } =
    useSelector((state) => state.order);
  const { latestOrder } = useSelector((state) => state.user);
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { id: userId, email, phoneNumber } = user;
  const { getFeatureValue } = useFeatureFlags();

  const useAutoRenewWithMomo = getFeatureValue(FEATURE_KEYS.AUTO_RENEW_MOMO, {
    userId,
    email,
    phoneNumber,
  });

  const isAutoRenewYearPackage = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const packageCode = queryParams.get('package');
    if (!packageCode) return false;

    const packageDuration = getPackageDuration(packageCode);

    return packageDuration === DURATION.YEARLY && isAutoRenew;
  }, [location.search]);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [prepaidPaymentMethods, setPrepaidPaymentMethods] = useState([]);
  const [rawAutoRenewalPaymentMethods, setRawAutoRenewalPaymentMethods] =
    useState([]);
  const [loading, setLoading] = useState(true);

  const handleSelectMethod = (method) =>
    dispatch(actions.order.updatePaymentMethod(method));

  const getPrepaidPaymentMethods = (methodsData, isVietNamCountry) => {
    let displayPaymentMethods = [];

    displayPaymentMethods = methodsData.filter(
      (method) =>
        method.active &&
        method.type !== PAYMENT_METHOD_TYPE.APPLE &&
        method.type !== PAYMENT_METHOD_TYPE.GOOGLE,
    );

    if (!isVietNamCountry)
      displayPaymentMethods = displayPaymentMethods.filter((method) =>
        GLOBAL_PAYMENT_METHOD.includes(method.type),
      );

    const mappedPaymentMethods = [];

    displayPaymentMethods.forEach((method) => {
      if (method.prepaidStatus !== AUTO_RENEW_STATUS.UNAVAILABLE) {
        mappedPaymentMethods.push({
          id: method.id,
          name: method.name[language],
          logo: method.icon,
          type: method.type,
          minPaymentAmount: method?.minPaymentAmount,
        });
      }
    });
    return mappedPaymentMethods;
  };

  const getAutoRenewalPaymentMethods = (methodsData = []) => {
    const displayPaymentMethods = methodsData.filter(
      (method) =>
        method.active &&
        method.type !== PAYMENT_METHOD_TYPE.APPLE &&
        method.type !== PAYMENT_METHOD_TYPE.GOOGLE &&
        method.type !== PAYMENT_METHOD_TYPE.BANK,
    );

    const mappedPaymentMethods = [];
    displayPaymentMethods.forEach((method) => {
      if (method.autoRenewStatus !== AUTO_RENEW_STATUS.UNAVAILABLE)
        mappedPaymentMethods.push({
          id: method.id,
          name: method.autoRenewName?.[language] || method.name[language],
          logo: method.autoRenewIcon || method.icon,
          autoRenewStatus: method.autoRenewStatus,
          type: method.type,
          rank: method.rank,
          isAutoRenewAvailable:
            method.autoRenewStatus === AUTO_RENEW_STATUS.AVAILABLE ? 1 : 0,
          source: method.source,
          minPaymentAmount: method?.minPaymentAmount,
        });
    });
    const sortedPaymentMethods = mappedPaymentMethods.sort(
      (a, b) =>
        b.isAutoRenewAvailable - a.isAutoRenewAvailable || a.rank - b.rank,
    );
    return sortedPaymentMethods;
  };

  const autoRenewalPaymentMethods = useMemo(
    () => getAutoRenewalPaymentMethods(rawAutoRenewalPaymentMethods),
    [rawAutoRenewalPaymentMethods, language],
  );

  const updateSelectedPrepaidPaymentMethod = (prepaidMethods) => {
    const paymentMethod = latestOrder?.paymentMethod || {};
    const bank = latestOrder?.bank;
    const type = paymentMethod?.type;
    const id = paymentMethod?.id;
    let selectPaymentMethod;
    if (id) {
      if (type !== PAYMENT_METHOD_TYPE.BANK)
        selectPaymentMethod = prepaidMethods.find((item) => item.id === id);
      else if (bank?.name) {
        selectPaymentMethod = prepaidMethods.find(
          (item) => item.name === bank?.name,
        );
      }
    }

    if (selectPaymentMethod?.name)
      dispatch(actions.order.updatePaymentMethod(selectPaymentMethod));
    else dispatch(actions.order.updatePaymentMethod(prepaidMethods[0] || {}));
  };

  const updatePaymentMethods = (
    prepaidMethods = prepaidPaymentMethods,
    autoRenewalMethods = autoRenewalPaymentMethods,
  ) => {
    if (isAutoRenew) {
      const availablePaymentMethod = autoRenewalMethods.find((method) => {
        const isMethodUpcoming =
          method.autoRenewStatus === AUTO_RENEW_STATUS.UPCOMING;

        const isMomoDisabled =
          method.type === PAYMENT_METHOD_TYPE.MOMOPAY &&
          (isAutoRenewYearPackage || (isAutoRenew && !useAutoRenewWithMomo));

        return !(isMethodUpcoming || isMomoDisabled);
      });

      dispatch(actions.order.updatePaymentMethod(availablePaymentMethod || {}));
    } else {
      updateSelectedPrepaidPaymentMethod(prepaidMethods);
    }

    setPaymentMethods(isAutoRenew ? autoRenewalMethods : prepaidMethods);
  };

  const fetchPaymentMethods = async () => {
    setLoading(true);
    let isVietNamCountry = true;
    try {
      const ipInfo = await apis.country.getCountry();
      if (ipInfo?.country && ipInfo?.country !== COUNTRY.VN)
        isVietNamCountry = false;
      else isVietNamCountry = true;
    } catch (error) {
      isVietNamCountry = true;
    }

    const data = await apis.paymentMethods.getPaymentMethods({
      sort: 'rank_asc',
    });
    if (data.status) {
      const { result } = data;

      const apiPaymentMethods = result.paymentMethods || [];

      const filteredMethods = apiPaymentMethods.filter(
        ({ paymentHub }) =>
          !Object.values(SMS_IDENTITY_PROVIDER).includes(
            paymentHub?.paymentType,
          ),
      );

      const prepaidMethods = getPrepaidPaymentMethods(
        filteredMethods,
        isVietNamCountry,
      );

      setPrepaidPaymentMethods(prepaidMethods);
      setRawAutoRenewalPaymentMethods(filteredMethods);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    if (Object.keys(packageState)) {
      updatePaymentMethods(prepaidPaymentMethods, autoRenewalPaymentMethods);
    }
  }, [
    packageState,
    prepaidPaymentMethods,
    autoRenewalPaymentMethods,
    isAutoRenew,
  ]);

  return (
    <StyledPaymentMethods>
      <Typography className="title-payment-method">
        {t('choosePaymentMethod')}
      </Typography>
      {useAutoRenewWithMomo && isAutoRenewYearPackage && (
        <Typography className="note">{t('momoAutoRenewNote')}</Typography>
      )}
      {loading ? (
        <Grid container spacing={1}>
          {Array.from(new Array(PAGINATION_LIMIT)).map(() => (
            <Grid item xs={12} sm={12} lg={6} xl={6} key={uuidv4()}>
              <Skeleton
                animation="wave"
                width="100%"
                height={90}
                sx={{ bgcolor: COLOR.grayV2[100] }}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={1}>
          {paymentMethods.map((method) => (
            <Grid
              item
              xs={12}
              sm={12}
              lg={12}
              xl={12}
              key={method.name}
              data-id="option-payment-method"
              data-payment-method={method?.type?.toLowerCase()}
            >
              <PaymentMethodItem
                method={method}
                isAutoRenewYearPackage={isAutoRenewYearPackage}
                isAutoRenew={isAutoRenew}
                selected={
                  selectedPaymentMethod?.name?.toLowerCase() ===
                  method.name?.toLowerCase()
                }
                useNewBankTransfer
                onSelect={handleSelectMethod}
                useAutoRenewWithMomo={useAutoRenewWithMomo}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </StyledPaymentMethods>
  );
};

export default PaymentMethods;
